import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDeleteChargePoint } from '../../../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../../../app/CurrentOrganisationProvider';
import { DangerButton } from '../../../../../../../components/DangerButton';
import { errorSnackbar } from '../../../../../../../components/ErrorSnackbar';
import { Card } from '../../../../../../../design-system';
import { SubState } from '../../../../../../../store/types';
import { useChargePoint } from '../../Provider';

const DeleteChargePointDialog: React.FC<{
  chargePoint: SubState<'chargePoint'>;
  organisation: SubState<'organisation'>;
}> = ({ chargePoint, organisation }) => {
  const [open, setOpen] = React.useState(false);

  const { push } = useHistory();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const { loading, mutate } = useDeleteChargePoint({});

  const onDelete = React.useCallback(() => {
    mutate(chargePoint.id)
      .then(() => {
        handleClose();
        push(`/organisations/${organisation.slug}/charge-points`);
      })
      .catch((e) => {
        errorSnackbar('Unable to delete charge point', closeSnackbar, enqueueSnackbar, e);
      });
  }, [chargePoint, closeSnackbar, enqueueSnackbar, mutate, organisation, push]);

  return (
    <Card title="Delete charge point">
      <Box p={2} pt={0}>
        <Typography paragraph>This will delete the charge point.</Typography>
        <Button color="inherit" onClick={handleClickOpen} variant="contained">
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete charge point'}
        </Button>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete charge point {chargePoint.name}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action cannot be undone. Are you sure you want to permanently delete the charge point?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={loading} onClick={handleClose} variant="contained">
            Cancel
          </Button>
          <DangerButton loading={loading} label="Delete charge point" onClick={onDelete} />
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export const DeleteChargePoint: React.FC = () => {
  const { organisation } = useCurrentOrganisation();
  const { chargePoint } = useChargePoint();

  if (!chargePoint || !organisation) {
    return <Card title="Delete charge point" />;
  }
  return <DeleteChargePointDialog chargePoint={chargePoint} organisation={organisation} />;
};
